/**
 * Metaballs - organic golden blobs that merge and separate.
 * Rendered on canvas using marching squares approximation.
 */

interface Ball {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
}

export function initMetaballs(canvas: HTMLCanvasElement): () => void {
  if (window.innerWidth < 768) return () => {};

  const ctxMaybe = canvas.getContext('2d');
  if (!ctxMaybe) return () => {};
  const ctx: CanvasRenderingContext2D = ctxMaybe;

  let running = true;
  let raf = 0;
  const BALL_COUNT = 8;
  const balls: Ball[] = [];
  const SCALE = 4;

  function resize() {
    canvas.width = Math.floor(canvas.offsetWidth / SCALE);
    canvas.height = Math.floor(canvas.offsetHeight / SCALE);
  }

  function init() {
    balls.length = 0;
    const w = canvas.width;
    const h = canvas.height;
    for (let i = 0; i < BALL_COUNT; i++) {
      balls.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.8,
        vy: (Math.random() - 0.5) * 0.8,
        r: 15 + Math.random() * 25,
      });
    }
  }

  function tick() {
    if (!running) return;
    const w = canvas.width;
    const h = canvas.height;

    for (const b of balls) {
      b.x += b.vx;
      b.y += b.vy;
      if (b.x < 0 || b.x > w) b.vx *= -1;
      if (b.y < 0 || b.y > h) b.vy *= -1;
      b.x = Math.max(0, Math.min(w, b.x));
      b.y = Math.max(0, Math.min(h, b.y));
    }

    const img = ctx.createImageData(w, h);
    for (let py = 0; py < h; py++) {
      for (let px = 0; px < w; px++) {
        let sum = 0;
        for (const b of balls) {
          const dx = px - b.x;
          const dy = py - b.y;
          sum += (b.r * b.r) / (dx * dx + dy * dy + 1);
        }
        if (sum > 1) {
          const intensity = Math.min(sum - 1, 1);
          const idx = (py * w + px) * 4;
          img.data[idx] = Math.floor(201 * intensity);
          img.data[idx + 1] = Math.floor(168 * intensity);
          img.data[idx + 2] = Math.floor(76 * intensity);
          img.data[idx + 3] = Math.floor(120 * intensity);
        }
      }
    }
    ctx.putImageData(img, 0, 0);

    raf = requestAnimationFrame(tick);
  }

  function onResize() {
    resize();
    init();
  }
  function onVisibility() {
    if (document.hidden) {
      cancelAnimationFrame(raf);
    } else {
      raf = requestAnimationFrame(tick);
    }
  }

  resize();
  init();
  window.addEventListener('resize', onResize);
  document.addEventListener('visibilitychange', onVisibility);
  tick();

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', onResize);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
